import { useState, useEffect } from "react";
import { useAppSelector } from "../../store/hooks";
import {
  useGetQuery,
  useAddMutation,
  useUpdateMutation,
  useRemoveMutation,
} from "../../store/slices/apiSlice";
import { Well } from "../../store/types";
import { useToast } from "../use-toast";
import { getErrorMessage } from "./use-getError";

interface WellsProps {
  fetchAll: boolean;
}

export const useWellsAPI = ({ fetchAll = false }: WellsProps) => {
  const wellsEP = useAppSelector((state) => state.endpoints.wells);
  const [wells, setWells] = useState<Well[] | null>(null);
  const { toastRef, showSuccess, showWarning, showError } = useToast();

  const [addItem, { isLoading: isAdding }] = useAddMutation();
  const [editItem, { isLoading: isEditing }] = useUpdateMutation();
  const [deleteItem, { isLoading: isDeleting }] = useRemoveMutation();

  const {
    data: fetchedWells,
    refetch: refetchWells,
    isLoading: isFetchLoading,
  } = useGetQuery(wellsEP, {
    refetchOnMountOrArgChange: true,
    skip: !wellsEP || !fetchAll,
  });

  useEffect(() => {
    if (fetchedWells) setWells(fetchedWells);
  }, [fetchedWells]);

  const refetchData = async () => {
    await refetchWells();
  };

  const handleError = (operation: string, error: any) => {
    showError(
      "Ошибка",
      `Не удалось ${operation}, ошибка: ${error}. Попробуйте позже.`
    );
  };

  const handlePostWell = async (well: Well): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: wellsEP,
        id: "",
        body: well,
      });
      if (result.data) {
        showSuccess("Успешно", "Скважина создана");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("создать скважину", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("создать скважину", error);
      return false;
    }
  };

  const handleUpdateWell = async (well: Well): Promise<boolean> => {
    try {
      const result = await editItem({
        endpoint: wellsEP,
        id: well?.uid,
        body: well,
      });
      if (result.data) {
        showSuccess("Успешно", "Данные скважины обновлены");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("обновить скважину", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("обновить скважину", error);
      return false;
    }
  };

  const handleDeleteWell = async (wellId: string): Promise<boolean> => {
    try {
      const result = await deleteItem({
        endpoint: wellsEP,
        id: wellId,
      });
      if (result.error) {
        let serverError = getErrorMessage(result.error);
        handleError("удалить скважину", serverError ?? result.error);
        return false;
      }

      showSuccess("Успешно", "Скважина удалена");
      return true;
    } catch (error) {
      handleError("удалить скважину", error);
      return false;
    }
  };

  return {
    wellsEP,
    isFetchLoading,
    wells,
    refetchData,
    handlePostWell,
    handleUpdateWell,
    handleDeleteWell,
    isAdding,
    isEditing,
    isDeleting,
    toastRef,
  };
};
